import { CheckboxAtom, colors, Stack, Typography } from '@ui-components/neo.tax';
import { CheckboxOptions } from 'ariakit';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import { useRdProjectGroupingTicketsOrCollections, RdProject } from '@neo.tax/rd/hooks';
import {
  KeywordSearch,
  RdGroupingTicketOrCollection,
  useKeywordSearch,
  GroupedTicketsComponentProps,
  TicketGroupingList,
} from '@neo.tax/shared';

import { GroupingSelection, useTicketSelections } from './hooks';
import { MoveRdTicketsButton } from './MoveRdTicketsButton';
import { Footer, MainContainer, StackFlexShrink } from './rd-tickets-pane.styled';
import { RdGroupedTickets } from './RdGroupedTickets';

export interface RdTicketsPaneProps {
  className?: string;
  rdProject: RdProject;
}

export const RdTicketsPane = ({ className, rdProject }: RdTicketsPaneProps): ReactElement => {
  const { searchKeyword, setSearchKeyword } = useKeywordSearch();
  const [groupingSelections, setGroupingSelections] = useState<Record<string, GroupingSelection>>({});
  const { fiscalYearId } = useRdContext();

  const { data, loading } = useRdProjectGroupingTicketsOrCollections({
    rdProjectId: rdProject.id,
    fiscalYearId,
    searchKeyword,
  });

  // Reset grouping selections when rdProject.id or searchKeyword changes
  useEffect(() => {
    setGroupingSelections({});
  }, [rdProject.id, searchKeyword]);

  const selectedGroupingsCount = useMemo(
    () => Object.values(groupingSelections).filter((selection) => selection.isGroupingSelected).length,
    [groupingSelections],
  );

  // Because we've deselected non-matching tickets, we can determine if they're
  // all selected by just checking the sizes of the collections. This can be
  // true, false, or 'mixed' if only some are selected.
  const selectAllChecked: CheckboxOptions['checked'] =
    selectedGroupingsCount === 0 ? false : selectedGroupingsCount === data?.length ? true : 'mixed';

  const doSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        // If we're checking the box, select all grouping tickets.
        const newSelections: Record<string, GroupingSelection> = {};
        data?.forEach((groupingTicketOrCollection) => {
          newSelections[groupingTicketOrCollection.id] = {
            isGroupingSelected: true,
            selectedTicketIds: new Set(),
          };
        });
        setGroupingSelections(newSelections);
      } else {
        // If we're un-checking the box, clear all selections.
        setGroupingSelections({});
      }
    },
    [data],
  );

  const selectedTicketCount = useMemo(() => {
    if (!data) return 0;

    let totalCount = 0;

    data.forEach((grouping) => {
      const selection = groupingSelections[grouping.id];
      if (selection?.isGroupingSelected) {
        totalCount += grouping.count;
      } else if (selection?.selectedTicketIds.size > 0) {
        totalCount += selection.selectedTicketIds.size;
      }
    });

    return totalCount;
  }, [data, groupingSelections]);

  const {
    groupingCollectionIds,
    groupingTicketIds,
    includeNoParentTickets,
    individualTicketIds,
    selectedItemsSummary,
  } = useTicketSelections({ data: data ?? [], groupingSelections });

  const BoundRdGroupedTickets = useMemo(
    () =>
      function BoundRdGroupedTickets(props: GroupedTicketsComponentProps): ReactElement {
        return (
          <RdGroupedTickets
            {...props}
            rdProjectId={rdProject.id}
            groupingTicketOrCollection={props.groupingTicketOrCollection as RdGroupingTicketOrCollection}
          />
        );
      },
    [rdProject.id],
  );

  return (
    <MainContainer className={className} direction="column">
      <StackFlexShrink paddingX={4} paddingY={3} gap={3}>
        <KeywordSearch searchKeyword={searchKeyword} setSearchKeyword={setSearchKeyword} />
      </StackFlexShrink>
      <TicketGroupingList
        GroupedTicketsComponent={BoundRdGroupedTickets}
        groupingTicketOrCollections={data ?? []}
        groupingSelections={groupingSelections}
        setGroupingSelections={setGroupingSelections}
        loading={loading}
        searchKeyword={searchKeyword}
        isMoveTicketsDisabled={false}
      />
      <>
        {selectedItemsSummary.hasSelections && (
          <StackFlexShrink backgroundColor={colors.gray[600]} paddingX={4} paddingY={1}>
            <Typography fontSize="xs" color={colors.gray[100]} textAlign="center">
              {selectedItemsSummary.text}
            </Typography>
          </StackFlexShrink>
        )}
        <Footer
          backgroundColor={colors.gray[500]}
          direction="row"
          justifyContent="space-between"
          paddingX={4}
          paddingY={4}
        >
          <Stack as="label" alignItems="center" gap={2}>
            {!searchKeyword && (
              <>
                <CheckboxAtom checked={selectAllChecked} onChange={doSelectAll} />
                <Typography fontWeight="medium" color={colors.white}>
                  Select All
                </Typography>
              </>
            )}
          </Stack>
          <MoveRdTicketsButton
            groupingCollectionIds={groupingCollectionIds}
            groupingTicketIds={groupingTicketIds}
            includeNoParentTickets={includeNoParentTickets}
            ticketIds={individualTicketIds}
            fromRdProjectId={rdProject.id}
            ticketCount={selectedTicketCount}
            onCompleted={() => setGroupingSelections({})}
          />
        </Footer>
      </>
    </MainContainer>
  );
};
